import React, { useContext, useEffect, useState } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { useStaticQuery, graphql, navigate } from 'gatsby'
import { currencyCode } from 'src/utils/constants'
import StoreContext from 'src/context/StoreContext'
import PropTypes from 'prop-types'
import Analytics from 'src/utils/Analytics'
import { CloseButton, PopupOverlay } from '../../Popup/styles'
import {
  PopupContainer,
  Heading,
  Text,
  TextLarge,
  TextMedium,
  Recommended,
  CtaContainer,
  MobileCtaContainer,
  CalendarImage,
  TextContainer,
  ThreeColumnContainer,
  ProductImage,
  ShowOnDesktop,
} from './styles'

import AddToCartButton from '../../AddToCartButton'
import { DisplayOnMobile } from '../../Cart/styles'

const UpsellPopup = ({
  selectedCalendar,
  selectedCalendarProductId,
  setDisplayToolTip,
  displayAddToCart,
}) => {
  const { closeIcon, desktopDeviceLinking, mobileDeviceLinking } = useStaticQuery(graphql`
    query UpsellPopupQuery {
      closeIcon: file(relativePath: { eq: "icons/close-icon.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 28, layout: FIXED, placeholder: NONE)
        }
      }
      desktopDeviceLinking: file(relativePath: { eq: "temp/calendar-10-device-syncing.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      mobileDeviceLinking: file(
        relativePath: { eq: "temp/calendar-10-device-syncing-mobile.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)

  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    Analytics.track(`Viewed Calendar Cross Sell Product Page`)
  }, [])

  const { addToCart, applyDiscount } = useContext(StoreContext)
  const productImage = selectedCalendar.images[0]
  const handleAddToCart = async (variantId, quantity) => {
    const id = variantId.split('/').pop()
    const pushToDataLayer = () => {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'addToCart',
        ecommerce: {
          currencyCode,
          add: {
            products: [
              {
                id,
                name: 'The Skylight Calendar',
                category: 'Calendar',
                price: 144.99,
                quantity,
                sku: '102CA',
                productId: id,
              },
            ],
          },
        },
      })
    }

    if (!submitting) {
      setSubmitting(true)
      Analytics.track(`Added Calendar to Cart`)
      Analytics.track(`Added Calendar Cross Sell Product to Cart`)
      const items = [{ variantId, quantity }]
      const resp = await addToCart(items)
      if (resp && resp.error) {
        setSubmitting(false)
        alert(resp.error)
      } else {
        setDisplayToolTip(false)
        pushToDataLayer(variantId, 1)
        setSubmitting(false)
        applyDiscount('DEVICE-LINKING-CAL-10')
        setTimeout(() => {
          navigate('/cart/')
        }, 250)
      }
    }
  }

  return (
    <PopupOverlay>
      <PopupContainer>
        <CloseButton
          onClick={() => {
            Analytics.track(`Clicked Close on Cross Sell Popup CTA`)
            setDisplayToolTip(false)
            setTimeout(() => {
              navigate('/cart/')
            }, 250)
          }}
        >
          <GatsbyImage image={closeIcon.childImageSharp.gatsbyImageData} alt="Close" />
        </CloseButton>
        <DisplayOnMobile>
          <TextLarge>Great Choice!</TextLarge>
        </DisplayOnMobile>
        <ThreeColumnContainer>
          <ShowOnDesktop>
            <Heading>🎉 Whoohoo! 🎉</Heading>
            <Recommended>You're on your way to more organized days...</Recommended>
          </ShowOnDesktop>
          <ProductImage image={productImage.gatsbyImageData} alt="Calendar 15in with Plus Plan" />
          <div>
            <ShowOnDesktop>
              <TextLarge>Great Choice!</TextLarge>
            </ShowOnDesktop>
            <TextMedium noMargin>The Skylight Calendar 15" with Plus Plan</TextMedium>
            <TextMedium noMargin>
              <s>$299.99</s>{' '}
              <span>
                <b>$279.99</b>
              </span>
            </TextMedium>
            <AddToCartButton
              xsButton
              label="View Cart"
              handleClick={() => {
                Analytics.track('Clicked View Cart on Cross Sell Popup CTA')
                setTimeout(() => {
                  navigate('/cart/')
                }, 250)
              }}
              oos={false}
              adding={false}
            />
          </div>
        </ThreeColumnContainer>
        <CalendarImage>
          <GatsbyImage
            className="desktop-only"
            image={desktopDeviceLinking.childImageSharp.gatsbyImageData}
            alt="Sync multiple calendars"
          />
          <GatsbyImage
            className="mobile-only"
            image={mobileDeviceLinking.childImageSharp.gatsbyImageData}
            alt="Sync multiple calendars"
          />
          <MobileCtaContainer>
            {displayAddToCart && (
              <AddToCartButton
                smallerButton
                label="Add & Check Out"
                handleClick={() => {
                  handleAddToCart(selectedCalendarProductId, 1)
                }}
                oos={false}
                adding={submitting}
              />
            )}
          </MobileCtaContainer>
          <TextContainer>
            <Recommended>
              Add a 10" Calendar <br className="desktop-only-break" /> for <s>$169.99</s>{' '}
              <span>$149.99</span>
            </Recommended>
            <Text>
              With device linking, you can connect multiple Calendars across rooms (or even
              households!), making it easier than ever for the whole family to see what’s going on
              in real time.
            </Text>
            <ShowOnDesktop>
              <CtaContainer>
                {displayAddToCart && (
                  <AddToCartButton
                    smallerButton
                    label="Add & Check Out"
                    handleClick={() => {
                      handleAddToCart(selectedCalendarProductId, 1)
                    }}
                    oos={false}
                    adding={submitting}
                  />
                )}
              </CtaContainer>
            </ShowOnDesktop>
          </TextContainer>
        </CalendarImage>
      </PopupContainer>
    </PopupOverlay>
  )
}

export default UpsellPopup

UpsellPopup.propTypes = {
  selectedCalendar: PropTypes.object,
  selectedCalendarProductId: PropTypes.string,
  setDisplayToolTip: PropTypes.func.isRequired,
  displayAddToCart: PropTypes.bool.isRequired,
}
